
import React from "react";
import { format } from "date-fns";
import { Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { useTheme } from "@/lib/theme/ThemeContext";
import { Database } from "@/types/supabase";

type Child = Database["public"]["Tables"]["children"]["Row"];

interface ChildCardProps {
  child: Child;
  onEdit: (child: Child) => void;
  onDelete: (childId: string) => void;
}

export function ChildCard({ child, onEdit, onDelete }: ChildCardProps) {
  const isLightTheme = false;

  return (
    <Card className="group relative overflow-hidden border-border/50 hover:border-primary/30 bg-card transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5">
      {/* Decorative gradient overlay */}
      <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 blur-2xl rounded-full -translate-y-8 translate-x-8 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      
      <CardHeader className="pb-3 relative">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-primary/10 group-hover:bg-primary/15 transition-colors duration-300">
            <svg 
              className="h-5 w-5 text-primary" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" 
              />
            </svg>
          </div>
          <CardTitle className="text-xl font-semibold tracking-tight">{child.name}</CardTitle>
        </div>
      </CardHeader>
      
      <CardContent className="relative">
        <div className="p-3 rounded-lg bg-muted/50 border border-border/40">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground font-medium">Date of Birth</span>
            <span className="font-semibold">
              {child.date_of_birth ? format(new Date(child.date_of_birth), 'PPP') : 'Not set'}
            </span>
          </div>
        </div>
      </CardContent>
      
      <CardFooter className="flex justify-end gap-2 pt-4 relative">
        <Button 
          variant="outline" 
          size="sm"
          className="border-border hover:bg-accent hover:border-primary/30 transition-all duration-200 hover:scale-105 active:scale-95"
          onClick={() => onEdit(child)}
        >
          <Edit className="h-4 w-4 mr-1.5" />
          Edit
        </Button>
        <Button 
          variant="destructive" 
          size="sm"
          className="transition-all duration-200 hover:scale-105 active:scale-95"
          onClick={() => onDelete(child.id)}
        >
          <Trash2 className="h-4 w-4 mr-1.5" />
          Remove
        </Button>
      </CardFooter>
    </Card>
  );
}
